import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { ApiError, policies, violationMessages, type ViolationMessage } from '../../lib/api'
import { previewNotice } from '../violationMessages/violationMessages'

interface Props {
  /** Null until the draft has been saved; a choice made before then is held by the page. */
  policyId: string | null
  /** The message chosen for an unsaved draft, used by the page on its first save. */
  pending: string | null
  onPendingChange: (violationMessageId: string | null) => void
  /** Whether the policy tells the person at all. A message on one that does not is refused at publish. */
  notifies: boolean
  /** Reported up so the enforcement form can stop offering words that would be replaced. */
  onChosenChange: (chosen: boolean) => void
}

/**
 * Which violation message this policy shows (ADR-S-053).
 *
 * Chosen by reference, like a dictionary: publication writes the message's current words into the
 * policy, so a corrected handbook section reaches the fleet by republishing rather than by editing
 * every policy that cites it. Choosing none keeps the words typed into this policy itself.
 *
 * Saves the moment it changes and does not republish, for the reason a dictionary binding does not.
 */
export function ViolationMessagePicker({ policyId, pending, onPendingChange, notifies, onChosenChange }: Props) {
  const [messages, setMessages] = useState<ViolationMessage[] | null>(null)
  const [chosen, setChosen] = useState<string | null>(policyId ? null : pending)
  const [failure, setFailure] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    violationMessages
      .list()
      .then((list) => setMessages(list.items))
      .catch((error: unknown) => setFailure(error instanceof ApiError ? error.message : 'The messages could not be read.'))
  }, [])

  useEffect(() => {
    if (!policyId) return
    policies
      .violationMessage(policyId)
      .then((bound) => setChosen(bound.violationMessageId))
      .catch((error: unknown) => setFailure(error instanceof ApiError ? error.message : 'The chosen message could not be read.'))
  }, [policyId])

  useEffect(() => onChosenChange(chosen !== null), [chosen, onChosenChange])

  const choose = async (next: string | null) => {
    setFailure(null)
    setNotice(null)
    if (!policyId) {
      setChosen(next)
      onPendingChange(next)
      setNotice('Held on this page. It is recorded when this draft is first saved.')
      return
    }
    setBusy(true)
    try {
      const saved = await policies.useViolationMessage(policyId, next)
      setChosen(saved.violationMessageId)
      setNotice(
        next === null
          ? 'This policy shows its own words again, from the next publish.'
          : 'Chosen. Devices show it from the next time this policy is published.'
      )
    } catch (error) {
      setFailure(error instanceof ApiError ? error.message : 'The message could not be chosen.')
    } finally {
      setBusy(false)
    }
  }

  const selected = messages?.find((message) => message.violationMessageId === chosen) ?? null
  // A message retired since it was chosen is not in the active list. It is still chosen, and the
  // policy's next publish is refused for it, so the page says so rather than showing "its own words".
  const missing = chosen !== null && messages !== null && selected === null

  return (
    <div className="field" data-testid="violation-message-picker">
      <label htmlFor="violation-message-choice">Words to show</label>
      <select
        id="violation-message-choice"
        value={chosen ?? ''}
        disabled={busy || messages === null}
        onChange={(event) => void choose(event.target.value || null)}
        data-testid="violation-message-choice"
      >
        <option value="">This policy's own words, below</option>
        {missing && <option value={chosen ?? ''}>A retired message</option>}
        {messages?.some((message) => !message.builtIn) && (
          <optgroup label="Your organization's">
            {messages
              .filter((message) => !message.builtIn)
              .map((message) => (
                <option key={message.violationMessageId} value={message.violationMessageId}>
                  {message.name}
                  {message.reference ? ` (${message.reference})` : ''}
                </option>
              ))}
          </optgroup>
        )}
        <optgroup label="Built in">
          {messages
            ?.filter((message) => message.builtIn)
            .map((message) => (
              <option key={message.violationMessageId} value={message.violationMessageId}>
                {message.name}
              </option>
            ))}
        </optgroup>
      </select>
      <div className="hint">
        A message from <Link to="/policies?tab=messages">your library</Link> cites the practice that was broken, in your
        handbook's words. Changing the message there changes this policy from its next publish.
      </div>

      {failure && (
        <div className="banner error" role="alert">
          {failure}
        </div>
      )}
      {notice && (
        <div className="banner success" role="status" data-testid="violation-message-chosen">
          {notice}
        </div>
      )}
      {missing && (
        <div className="banner error" data-testid="violation-message-retired">
          The message this policy uses was retired, so publishing it is refused. Choose another.
        </div>
      )}
      {chosen !== null && !notifies && (
        <div className="banner warning" data-testid="violation-message-unshown">
          This policy does not tell the person anything, so the message would never be shown and publishing is refused.
          Tick "Tell the person why", or go back to this policy's own words.
        </div>
      )}

      {selected && (
        <div className="notice-preview" data-testid="violation-message-preview" aria-label="What the person sees">
          <strong>{previewNotice(selected.title, selected.reference ?? '')}</strong>
          <span>{previewNotice(selected.message, selected.reference ?? '')}</span>
        </div>
      )}
    </div>
  )
}
